/**
 * 작성자 : 홍선영
 * 날짜 : 2024.10.29
 * 경로 : 인풋 로우리스트 컴포넌트
 */

import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { t } from 'i18next';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { userState } from '../../atoms';
import { IModal } from 'customTypes';
import { InputTable } from '../../assets/styles/InputTable';
import { BtnBlue, BtnRed } from '../Button';
import Input from '../Input';
import Portal from '../Portal';
import DeleteModal from '../Modal/DeleteModal2';
import { useSetAuth } from '../../utils/useSetAuth';
import { applyBorderStyle } from '../../utils/applyBorderStyle';
import { scrollToNodeBottom } from '../../utils/scrollToNodeBottom';
import { toast } from 'react-toastify';
import { ContentsContainerRoot } from '../../assets/styles/ContentsContainerRoot';
import { LoadingModalBackground } from '../../assets/styles/Modal';
import { PulseLoader } from 'react-spinners';
import IssueGuide from '../IssueGuide';
import { logPost } from '../../services/log';

const Root = styled(ContentsContainerRoot)`
  .inputFormsWrapper.smallTab {
    flex-direction: row;
    align-items: center;
    padding: 0 0.5rem;
    font-weight: 500;
    &::-webkit-scrollbar {
      -webkit-appearance: none;
      height: 0rem !important;
    }

    .tab {
      color: var(--text-secondary);
      display: flex;
      align-items: center;
      padding: 0 0.5rem;
      cursor: pointer;
      height: 3.5rem;
      border-bottom: 2px solid transparent;
    }
    .activeTab {
      font-weight: 700;
      border-bottom: 2px solid var(--selected-primary);
      color: var(--selected-primary);
    }
  }

  .tr > div > div {
    width: 100%;
    li {
      width: 100% !important;
    }
  }

  .secondSearchOption {
    width: fit-content;
  }

  .textBtnGroup button {
    padding: 0 0.75rem;
    width: 4.5rem;
    height: 2.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    border: 1px solid var(--outline);
    background-color: var(--tonal-light);
  }
`;

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

interface ItuList {
  bigo: string;
  tuCd: string;
  tuName: string;
  tuTel: string;
  useYn?: string;
  writer?: string;
  wDate?: string;
  editor: string;
  eDate?: string;
  tuGubun?: string;
  flag?: string;
}

interface IRowHeader {
  idx: number;
  className: string;
  headerName: string;
  inputType: 'disabledInput' | 'text' | 'tel';
  dataKey: string;
  required: boolean;
  maxLength?: number;
}

interface IRowList {
  rowPk: string;
  initRow: any;
  data: any[];
  isLoading: boolean;
  isError: boolean;
  rowHeaderList: IRowHeader[];
  postAPI: (array: any[]) => void;
  deleteAPI: (code: string) => void;
}

const InputRowList = ({ rowPk, initRow, data, isLoading, isError, rowHeaderList, postAPI, deleteAPI }: IRowList) => {
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const userInfo = useRecoilValue(userState);
  const [tableState, setTableState] = useState<any[]>(data);
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const scrollContainerRef = useRef<HTMLInputElement>(null);
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const inputRefs = useRef<HTMLInputElement[] | null>(Array(tableState.length).fill(null));
  const tableStateRef = useRef(tableState);

  useEffect(() => {
    if (data) setTableState(data);
  }, [data]);

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '(사내) 설정관리 > 고객사관리 > 현장 정보',
      action: '조회',
      etc: ``,
    });
  }, []);

  useEffect(() => {
    tableStateRef.current = tableState;
  }, [tableState]);

  useEffect(() => {
    if (tableState.length > 0) scrollToNodeBottom(scrollContainerRef);
  }, [tableState.length]);

  const onClickAddTableRow = () => {
    const newObj = { ...initRow, editor: userInfo.userId };
    setTableState((prev: ItuList[]) => [...prev, newObj]);
  };

  // 원본데이터와 비교하여 수정사항이 있는지 판별하여 수정값이 있는 객체들을 배열로 리턴하는 함수
  const findDifferences = (original: any[], updated: any[], uniqueKey: string) => {
    const updates = updated.filter((upd) => {
      const orgEntry = original.find((org) => org[uniqueKey] === upd[uniqueKey]);
      return orgEntry ? Object.keys(upd).some((key) => upd[key] !== orgEntry[key]) : true;
    });

    return updates;
  };

  const onClickSave = () => {
    setIsSaveClicked(true);

    const required = rowHeaderList.filter((el) => el.required).map((el: any) => el.dataKey);
    const emptyCheck = tableState.filter((el: any) => {
      const check = required.find((key: keyof any) => el[key] === '');
      return check !== undefined;
    });
    // const telFocusIndex = tableState.findIndex((el) => el.chTel.length < 10);

    if (emptyCheck.length > 0) {
      toast.warning(t('필수입력값을 모두 입력하세요'));
    }
    // else if (telFocusIndex !== -1) {
    //   // 휴대폰번호 9자리 이하일경우 포커스
    //   // Delay the focus to allow for state update
    //   setTimeout(() => {
    //     inputRefs.current && inputRefs.current[telFocusIndex]?.focus();
    //     toast.warning(t('휴대폰 번호를 확인하세요.'));
    //   }, 0);
    // }
    else {
      const updatedArray = findDifferences(data, tableState, rowPk);
      if (updatedArray.length > 0) postAPI(updatedArray);
      else toast.warning(t('변경사항이 없습니다'));
    }
  };

  const onClickDelete = (el: any, i: number) => {
    if (el[rowPk]) {
      // 기존 데이터 삭제시
      const obj = { ...el, editor: userInfo.userId };
      setOpenModal((prev) => ({ ...prev, type: 'delete', status: true, state: tableState, setState: setTableState, api: () => deleteAPI(obj.chCd), el: obj, index: i }));
    } else {
      // 제거 (새로 추가한 데이터 제거시)
      const updatedArray = [...tableState];
      updatedArray.splice(i, 1);
      setTableState(updatedArray);
    }
  };

  const renderInputComponent = (index: number, inputType: 'disabledInput' | 'text' | 'tel', key: string, value: string, required: boolean, maxLength?: number) => {
    switch (inputType) {
      case 'disabledInput':
        return value;
      case 'tel':
        return <TelNumInput index={index} state={tableState} setState={setTableState} stateKey={key} lengthState={{ num1: 3, num2: 4, num3: 4 }} />;
      case 'text':
        return (
          <Input
            type='text'
            stateType={{ type: 'array', index }}
            id={key}
            name={key}
            state={tableState}
            setState={setTableState}
            maxLength={maxLength}
            getBorderStyle={isSaveClicked && required ? applyBorderStyle(value, 'red', key) : undefined}
          />
        );
      default:
        return value;
    }
  };

  if (isError) return <IssueGuide />;
  if (isLoading)
    return (
      <LoadingModalBackground>
        <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
      </LoadingModalBackground>
    );

  return (
    <div className='content-container oneColumn'>
      <Root loading={isLoading}>
        <InputTable className='margin-left-05' ref={scrollContainerRef}>
          <div className='thead'>
            <div className='tr'>
              {rowHeaderList.map(({ idx, className, headerName, required }) => (
                <div key={idx} className={`${className} ${required ? 'required' : ''}`}>
                  {t(headerName)}
                </div>
              ))}
              {auth.deleteAuth && <div className='trCol4 flex-center'> </div>}
            </div>
          </div>
          <div className='table' style={{ height: '100vh' }}>
            <div className='tbody'>
              {tableState.map((el: any, index: number) => (
                <div className='tr' key={index}>
                  {rowHeaderList.map(({ idx, className, inputType, dataKey, maxLength, required }) => (
                    <div key={idx} className={className}>
                      {renderInputComponent(index, inputType, dataKey, el[dataKey], required, maxLength)}
                    </div>
                  ))}
                  {auth.deleteAuth && (
                    <div className='trCol4 flex-center'>
                      <BtnRed onClick={() => onClickDelete(el, index)}>{el[rowPk] ? t('삭제') : t('제거')}</BtnRed>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </InputTable>
        <ButtonsWrapper>
          {auth.createAuth && <BtnBlue onClick={onClickAddTableRow}>{t('신규항목 추가')}</BtnBlue>}
          {(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}
        </ButtonsWrapper>
      </Root>
      {isLoading ? (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      ) : undefined}
      <Portal openModal={openModal?.status}>{openModal && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </div>
  );
};

interface ITelNumInput {
  index: number;
  state: any;
  setState: Dispatch<SetStateAction<any>>;
  stateKey: string;
  lengthState: any;
  isFirstInputFocused?: boolean;
  setIsLastInputCompleted?: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
  isSaveClicked?: boolean;
}

const TelNumInput = ({ index, state, setState, stateKey, lengthState, isFirstInputFocused, setIsLastInputCompleted, disabled = false, isSaveClicked }: ITelNumInput) => {
  const firstInputRef = useRef<HTMLInputElement>(null); // 첫번째 칸 인풋 레프
  const minInputRef = useRef<HTMLInputElement>(null); // 두번째 칸 인풋 레프
  const lastInputRef = useRef<HTMLInputElement>(null); // 마지막 칸 인풋 레프
  const [telNumState, setTelNumState] = useState({ num1: '', num2: '', num3: '' });

  useEffect(() => {
    const value = state[index]?.[stateKey] || '';
    const numsArray = value.split('-');
    setTelNumState({
      num1: numsArray[0] || '',
      num2: numsArray[1] || '',
      num3: numsArray[2] || '',
    });
  }, [state[index][stateKey]]);

  useEffect(() => {
    if (isFirstInputFocused) {
      firstInputRef.current?.focus();
    }
  }, [isFirstInputFocused]);

  // input의 길이가 maxLength를 충족하면, 다음 인풋칸으로 포커스 이동
  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>, nextRef: any, maxLength: number) => {
    const { name, value } = e.currentTarget;

    let newValue = value?.replace(/[^\d]/g, '');

    if (newValue.length > maxLength) {
      newValue = newValue.slice(0, maxLength);
    }
    if (nextRef !== null) {
      if (newValue.length === maxLength) nextRef.current?.focus();
    }
    if (name === 'num3') {
      if (newValue.length >= lengthState.num3) {
        setIsLastInputCompleted && setIsLastInputCompleted(true);
      } else {
        setIsLastInputCompleted && setIsLastInputCompleted(false);
      }
    }

    setTelNumState((prev) => ({ ...prev, [name]: newValue }));

    const copyState = [...state];
    let nums;
    if (name === 'num1') nums = `${value}-${telNumState.num2}-${telNumState.num3}`;
    if (name === 'num2') nums = `${telNumState.num1}-${value}-${telNumState.num3}`;
    if (name === 'num3') nums = `${telNumState.num1}-${telNumState.num2}-${value}`;

    copyState[index][stateKey] = nums;
    setState(copyState);
  };

  return (
    <div className='flex-basic'>
      <input
        className='text-center'
        type='text'
        name='num1'
        value={telNumState.num1}
        ref={firstInputRef}
        onChange={(e) => onChangeInput(e, minInputRef, lengthState.num1)}
        disabled={disabled}
        style={isSaveClicked ? applyBorderStyle(telNumState.num1, 'red', 'num1') : undefined}
        maxLength={3}
      />
      <span className='dash'>-</span>
      <input
        className='text-center'
        type='text'
        name='num2'
        value={telNumState.num2}
        ref={minInputRef}
        onChange={(e) => onChangeInput(e, lastInputRef, lengthState.num2)}
        disabled={disabled}
        style={isSaveClicked ? applyBorderStyle(telNumState.num2, 'red', 'num2') : undefined}
        maxLength={4}
      />
      <span className='dash'>-</span>
      <input
        className='text-center'
        type='text'
        name='num3'
        value={telNumState.num3}
        ref={lastInputRef}
        onChange={(e) => onChangeInput(e, null, lengthState.num3)}
        disabled={disabled}
        style={isSaveClicked ? applyBorderStyle(telNumState.num3, 'red', 'num3') : undefined}
        maxLength={4}
      />
    </div>
  );
};

export default InputRowList;
